import { useMemo } from "react";
import { comics, categories } from "@/mocks/comics";
import SectionHeading from "./SectionHeading";

interface FeaturedProps {
  genre: string;
  setGenre: (g: string) => void;
  search: string;
}

export default function Featured({ genre, setGenre, search }: FeaturedProps) {
  const genres = ["全部", ...categories.map((c) => c.name)];

  const list = useMemo(() => {
    let result = [...comics];
    if (genre !== "全部") result = result.filter((c) => c.genre === genre);
    const kw = search.trim();
    if (kw) {
      const lower = kw.toLowerCase();
      result = result.filter(
        (c) => c.title.toLowerCase().includes(lower) || c.tags.some((t) => t.includes(lower)),
      );
    }
    return result;
  }, [genre, search]);

  return (
    <section id="featured" className="relative py-14 md:py-20 bg-background-50">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="Featured 热门精选"
          title="精选热门高口碑韩漫"
          desc="从人气榜单中甄选最具看点的作品，画面、剧情与口碑兼备，点击海报即可查看详情。"
        />

        {/* filter chips */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-8 md:mb-10">
          {genres.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGenre(g)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                genre === g
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              {g}
            </button>
          ))}
        </div>

        {search && (
          <p className="text-center text-sm text-secondary-700 mb-6">
            为你找到与「<strong>{search}</strong>」相关的作品 {list.length} 部
          </p>
        )}

        {list.length === 0 ? (
          <div className="text-center py-16">
            <i className="ri-search-line text-4xl text-foreground-300"></i>
            <p className="mt-3 text-foreground-500">没有找到相关作品，换个关键词试试吧</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {list.map((c) => (
              <article
                key={c.id}
                className="group flex flex-col sm:flex-row gap-4 p-4 rounded-2xl bg-background-100 border border-background-200 hover:border-accent-300 hover:shadow-sm transition-all"
              >
                <div className="relative w-full sm:w-[140px] shrink-0">
                  <img
                    src={c.cover}
                    alt={`${c.title} 韩国动态漫画海报`}
                    className="w-full h-48 sm:h-[150px] object-cover object-top rounded-xl"
                  />
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-primary-500/90 text-background-50 text-[11px] font-bold">
                    {c.status}
                  </span>
                </div>

                <div className="flex-1 min-w-0 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-base md:text-lg font-bold text-foreground-950 leading-snug">{c.title}</h3>
                    <span className="inline-flex items-center gap-1 text-accent-600 font-black text-sm shrink-0">
                      <i className="ri-star-fill text-accent-500"></i>
                      {c.rating.toFixed(1)}
                    </span>
                  </div>
                  <p className="text-xs text-foreground-500 mt-1">{c.author} · {c.genre} · 共{c.episodes}话</p>

                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {c.tags.map((t) => (
                      <span key={t} className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-800 text-[11px] font-medium">
                        {t}
                      </span>
                    ))}
                  </div>

                  <p className="text-xs text-foreground-600 leading-relaxed mt-2.5 line-clamp-2 flex-1">{c.desc}</p>

                  <a
                    href="#contact"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-primary-600 hover:text-primary-700 cursor-pointer"
                  >
                    查看追更渠道
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}